import React, { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'antd';
import Button from './Button';
import AudioPreview from '../layout/AudioPreview';
import ImagePreview from '../layout/ImagePreview';
import VideoPreview, { Modes } from '../layout/VideoPreview';
import ImageCropDialog from './ImageCropDialog';

const LocalFileInput = (props) => {
  const fileInputRef = useRef();
  const [imageToCrop, setImageToCrop] = useState(null);

  const { onChange, cropSize } = props;

  const handleCancelCrop = useCallback(() => setImageToCrop(null), []);

  const handleChange = useCallback(
    (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      if (cropSize && ['image/jpeg', 'image/png'].includes(file.type)) {
        setImageToCrop(file);
        event.target.value = '';
        return;
      }
      if (onChange) {
        onChange(file);
      }
    },
    [onChange, cropSize]
  );

  const handleDelete = useCallback(() => {
    if (onChange) {
      onChange(null);
    }
    fileInputRef.current.value = '';
  }, [onChange]);

  const handleDownload = useCallback(() => {
    let filePath;
    const link = document.createElement('a');
    if (props.value instanceof File) {
      filePath = URL.createObjectURL(props.value);
      link.href = filePath;
      link.download = props.value.name;
    } else {
      filePath = `${process.env.REACT_APP_FILES_URL}/${props.value}`;
      link.href = filePath;
      link.download = filePath.split('/').pop();
    }
    link.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(filePath);
      link.remove();
    }, 500);
  }, [props.value]);

  const getAcceptAttribute = useCallback(() => {
    const allAcceptTypes = {
      audio: 'audio/mpeg,audio/mp3',
      image: 'image/jpeg,image/png',
      icon: 'image/x-icon',
      video: 'video/mpeg,video/mp4',
      pdf: 'application/pdf',
      subtitle: 'webvtt,srt',
      docs: 'application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint',
      html: 'text/html,.html,.htm',
    };

    const selectedAcceptTypes = [];
    for (const acceptType of props.accept.split(',')) {
      selectedAcceptTypes.push(allAcceptTypes[acceptType.trim()]);
    }

    return selectedAcceptTypes.join(',');
  }, [props.accept]);

  const fileType = useMemo(() => {
    if (!props.value) {
      return '';
    }
    if (props.value instanceof File) {
      if (
        props.value.type === 'text/html' ||
        ['html', 'htm'].includes(props.value.name.split('.').pop().toLowerCase())
      ) {
        return 'html';
      }
      return props.value.type.split('/')[0];
    }

    return {
      mp3: 'audio',
      wav: 'audio',
      mp4: 'video',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
      pdf: 'pdf',
      srt: 'subtitle',
      html: 'html',
      htm: 'html',
    }[props.value.split('.').pop().toLowerCase()];
  }, [props.value]);

  const hasValue = !!props.value;
  return (
    <>
      {imageToCrop && (
        <ImageCropDialog
          file={imageToCrop}
          cropSize={cropSize}
          onCancel={handleCancelCrop}
          onConfirm={(file) => {
            setImageToCrop(null);
            if (onChange) {
              onChange(file);
            }
          }}
        />
      )}
      <Form.Item
        colon={false}
        label={props.label}
        required={props.required}
        help={props.errorText}
        validateStatus={props.errorText && 'error'}
      >
        <div style={{ marginBottom: -40 }}>
          <Button
            label={props.buttonLabel || 'Select a file'}
            disabled={props.disabled}
            onClick={() => fileInputRef.current.click()}
            icon="upload"
          />{' '}
          {hasValue && (
            <>
              {!props.disabled && (
                <>
                  <Button tooltip="Delete the file" icon="delete" onClick={handleDelete} />{' '}
                </>
              )}
              {fileType === 'audio' && <AudioPreview src={props.value} />}
              {fileType === 'image' && <ImagePreview src={props.value} />}
              {fileType === 'video' && (
                <>
                  {props.value instanceof File && <span>{props.value.name}</span>}
                  {typeof props.value === 'string' && (
                    <VideoPreview src={props.value} id={props.value} mode={Modes.BUTTON_MODAL} />
                  )}
                </>
              )}
              {fileType === 'pdf' && <Button icon="download" onClick={handleDownload} />}
              {fileType === 'subtitle' && <Button icon="download" onClick={handleDownload} />}
              {fileType === 'html' && <Button icon="download" onClick={handleDownload} />}
            </>
          )}
        </div>

        <input
          type="file"
          style={{
            visibility: 'hidden',
            width: 0,
            height: 0,
          }}
          onChange={handleChange}
          ref={fileInputRef}
          accept={`${getAcceptAttribute()}`}
        />
      </Form.Item>
    </>
  );
};

LocalFileInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),
  onChange: PropTypes.func,
  label: PropTypes.node,
  buttonLabel: PropTypes.string,
  disabled: PropTypes.bool,
  errorText: PropTypes.string,
  accept: PropTypes.string,
  required: PropTypes.bool,
  cropSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
};

LocalFileInput.defaultProps = {
  accept: 'audio',
  value: null,
  errorText: null,
  disabled: false,
  required: false,
  cropSize: null,
};

export default LocalFileInput;
